import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import { createSpotterTruck, SpotterTruck } from '../../pages/YardSpotterApp/api';

interface SpotterTruckDialogProps {
  open: boolean;
  facilityId: string | null;
  trucks: SpotterTruck[];
  currentName?: string | null;
  onClose: () => void;
  onSaved: (truck: SpotterTruck) => void;
}

const normalize = (name: string) => name.trim().toLowerCase();

// Asks for the spotter truck this device runs yard checks from. Picks one of
// the account's trucks, or adds the typed number as a new truck.
const SpotterTruckDialog: React.FC<SpotterTruckDialogProps> = ({
  open,
  facilityId,
  trucks,
  currentName,
  onClose,
  onSaved,
}) => {
  const [input, setInput] = useState<string>(currentName || '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInput(currentName || '');
      setError(null);
    }
  }, [open, currentName]);

  const typed = input.trim();
  const existing = trucks.find(t => normalize(t.name) === normalize(typed));

  const handleSave = async () => {
    if (!typed || !facilityId) return;

    if (existing) {
      onSaved(existing);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      onSaved(await createSpotterTruck(facilityId, typed));
    } catch (err: any) {
      setError(err?.message || 'Failed to save spotter truck');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShipping />
          Spotter Truck
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the number of the spotter truck you are driving. It is saved on this device for
          later yard checks.
        </Typography>
        <Autocomplete
          freeSolo
          options={trucks.map(t => t.name)}
          inputValue={input}
          onInputChange={(_event, value) => setInput(value)}
          renderInput={params => (
            <TextField {...params} autoFocus label="Spotter truck number" placeholder="e.g. T63389" />
          )}
        />
        {typed && !existing && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            "{typed}" is not a known truck yet. Saving adds it.
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!typed || !facilityId || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : existing || !typed ? 'Save' : `Add truck "${typed}"`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpotterTruckDialog;
